/** @jsx jsx */
import { SliderHandle, SliderInput, SliderTrack, SliderTrackHighlight } from '@reach/slider'
import '@reach/slider/styles.css'
import * as Sentry from '@sentry/browser'
import _ from 'lodash'
import { rgba } from 'polished'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { jsx, SxStyleProp } from 'theme-ui'
import {
  Box,
  Button,
  Flex,
  FormattedCurrency,
  Txt,
  Row,
  Col,
  LoadingPlaceholder,
} from '../../components'
import { AsyncActionButton } from '../../components/AsyncActionButton'
import { FormattedPercentage } from '../../components/FormattedPercentage'
import { PlayingCard } from '../../components/PlayingCard'
import {
  ClientGameHandController,
  IClientUserData,
} from '../../controllers/ClientGameHandController'
import theme from '../../gatsby-plugin-theme-ui'
import { globalDebugger } from '../../utils/globalDebugger'
import { GameMockType, IGameActions } from './types'
import { captureExceptionWithLabel } from '../../utils/sentry'
import { getFunction } from '../../utils/api'
import { IPositioningMeasurements } from './positionProps'
import { getUserHandSolution } from '../../utils/getUserHandSolution'
import { getCanManuallyShowHand } from '../../utils/gameHandUtils'
import { numericBreakpoints } from '../../gatsby-plugin-theme-ui/theme'

export const RaiseOption: React.FC<{
  currentUserData: IClientUserData
  controller: ClientGameHandController
  actions: IGameActions
  disabled: boolean
  raiseInputRef: React.RefObject<HTMLInputElement>
  setIsShowingRaise?: (bool: boolean) => void
}> = ({ controller, actions, currentUserData, raiseInputRef, setIsShowingRaise, ...props }) => {
  const { min, max } = useMemo(() => {
    const min = controller.minRaiseForUser(currentUserData.id)
    const max = controller.maxRaiseForUser(currentUserData.id)
    return { min, max }
  }, [controller])
  const [amountToRaise, setAmountToRaise] = useState(min)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (amountToRaise !== min) {
      setAmountToRaise(min)
    }
  }, [min])
  const amountToCall = controller.amountRemainingForUserToCall(currentUserData.id)
  const totalAmount = amountToRaise + amountToCall
  const disabled = props.disabled || !max
  const parseInputValue = (val: string) => {
    const parsed = !val ? 0 : parseInt(val, 10)
    if (_.isFinite(parsed)) {
      return parsed
    }
    return null
  }
  const raiseInput = (
    <Box
      onClick={() => raiseInputRef.current?.focus()}
      as="span"
      sx={{
        px: 2,
        bg: 'gray.1',
        width: `${amountToRaise}`.length * 9 + 50,
        display: 'inline-block',
        color: 'text',
        borderRadius: 'sm',
        py: 1,
      }}
    >
      $
      <input
        ref={raiseInputRef}
        disabled={disabled}
        sx={{
          width: 'calc(100% - 10px)',
          fontSize: 1,
          color: 'text',
          bg: 'transparent',
          appearance: 'none',
          border: 'none',
        }}
        value={!amountToRaise ? '' : amountToRaise}
        onBlur={() => {
          if (amountToRaise < min || amountToRaise > max) {
            setAmountToRaise(min)
          }
        }}
        onChange={(e) => {
          const parsedVal = parseInputValue(e.target.value)
          if (!_.isNil(parsedVal) && e.target.value.length < `${max}`.length + 3) {
            setAmountToRaise(parsedVal)
          }
        }}
      />
    </Box>
  )
  const betOption = controller.userBetOptions(currentUserData.id)
  return (
    <Box
      as="form"
      onSubmit={async (e) => {
        e.preventDefault()
        let chipsAmount = amountToRaise
        if (raiseInputRef.current && document.activeElement === raiseInputRef.current) {
          const parsedAmount = parseInputValue(raiseInputRef.current.value)
          if (!_.isNil(parsedAmount)) {
            chipsAmount = parsedAmount
          } else {
            return
          }
        }
        if (chipsAmount > max) {
          setAmountToRaise(max)
          return
        }
        if (chipsAmount < min) {
          setAmountToRaise(min)
          return
        }
        try {
          setLoading(true)
          await actions.bet({ chipsAmount: chipsAmount + amountToCall })
          setLoading(false)
        } catch (e) {
          captureExceptionWithLabel(e, 'CurrentUserHand raise')
          setLoading(false)
        }
      }}
      sx={{ opacity: disabled ? 0.2 : 1 }}
    >
      <Txt sx={{ textAlign: 'center', fontSize: [0, 0, 1], color: 'lightText' }}>
        {currentUserData.state === 'ALL_IN' || currentUserData.amountLeftToBet < amountToCall ? (
          'All in'
        ) : currentUserData.state === 'FOLDED' ? (
          'Folded'
        ) : (
          <React.Fragment>
            {!!amountToCall && (
              <React.Fragment>
                Call{' '}
                <Txt sx={{ color: 'text' }} as="span">
                  <FormattedCurrency amount={amountToCall} />
                </Txt>{' '}
                and{' '}
              </React.Fragment>
            )}
            {betOption === 'BET' ? 'Bet' : 'Raise'} {raiseInput}{' '}
            {totalAmount === currentUserData.amountLeftToBet ? (
              <Txt sx={{ color: 'text' }} as="span">
                (All-in)
              </Txt>
            ) : (
              <React.Fragment>
                (
                <FormattedPercentage amount={totalAmount / currentUserData.amountLeftToBet} /> of
                chips)
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </Txt>
      <Row sx={{ alignItems: 'center' }}>
        <Col xs={12} lg={9}>
          <Flex sx={{ alignItems: 'center' }}>
            <Txt
              sx={{
                color: 'lightText',
                fontWeight: 'light',
                fontSize: 1,
                flex: '0 0 60px',
                textAlign: 'center',
              }}
            >
              <FormattedCurrency amount={min + amountToCall} />
            </Txt>
            <SliderInput
              key={min}
              disabled={disabled}
              min={0}
              max={200}
              step={10}
              {...{ min, max }}
              onChange={setAmountToRaise}
              defaultValue={min}
              sx={{
                width: '100% !important',
                height: '10px !important',
                paddingBottom: '18px !important',
                paddingTop: '8px !important',
                paddingLeft: '20px !important',
                paddingRight: '20px !important',
                opacity: max === min ? 0.2 : 1,
              }}
            >
              <SliderTrack
                sx={{
                  background:
                    'linear-gradient(90deg, rgba(11,45,35,0.7805497198879552) 0%, rgba(61,201,160,1) 100%)',
                  borderRadius: 'pill',
                  boxShadow: `0px 0px 2px 1px ${rgba(theme.colors.lightGreen, 0.9)}`,
                }}
              >
                <SliderTrackHighlight sx={{ bg: 'lightGreen' }} />
                <SliderHandle
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: 'pill',
                    border: 'none',
                    bg: 'lightGreen',
                    boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.15)',
                    color: 'white',
                    textShadow: `0px 0px 2px rgba(0,0,0,.2), 1px 1px 6px rgba(0,0,0,.2)`,
                  }}
                >
                  $
                </SliderHandle>
              </SliderTrack>
            </SliderInput>
            <Txt
              sx={{
                color: 'lightText',
                fontWeight: 'light',
                fontSize: 1,
                flex: '0 0 60px',
                textAlign: 'center',
              }}
            >
              <FormattedCurrency amount={max + amountToCall} />
            </Txt>
          </Flex>
        </Col>
        <Col xs={12} lg={3}>
          <Row>
            {!!setIsShowingRaise && (
              <Col xs={{ size: 6, offset: 0 }} md={{ size: 4, offset: 2 }}>
                <Button
                  sx={{
                    py: 3,
                    px: 1,
                    my: [4, null, null, null, 0],
                    bg: 'transparent',
                    color: 'darkBlue',
                  }}
                  block
                  onClick={() => setIsShowingRaise(false)}
                >
                  Close
                </Button>
              </Col>
            )}
            <Col xs={{ size: 6, offset: 0 }} md={{ size: 4, offset: 0 }} lg={12}>
              <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                <Button
                  block
                  type="submit"
                  sx={{ py: 3, px: 1, my: [4, null, null, null, 0] }}
                  disabled={disabled || loading}
                >
                  <LoadingPlaceholder loadingVariant="light" loading={loading}>
                    Bet <FormattedCurrency amount={totalAmount} />
                  </LoadingPlaceholder>
                </Button>
              </Box>
            </Col>
          </Row>
        </Col>
      </Row>
    </Box>
  )
}

const getCurrentUserData = (controller: ClientGameHandController) => {
  if (globalDebugger.hasTestingEnvironmentUX) {
    return controller.getUserData(controller.gameHand.nextUserIdToAct)
  }
  return controller.currentUserData
}

const actionButtonStyles: SxStyleProp = {
  width: [80, 100, null, null, 125],
  px: 0,
  display: 'block',
}

export const CurrentUserHand: React.FC<{
  controller: ClientGameHandController
  actions: IGameActions
  gameMockType?: GameMockType
  measurements: IPositioningMeasurements
}> = ({ controller, actions, gameMockType, measurements }) => {
  const { currentUserHand } = controller
  const solution = useMemo(() => {
    return (
      currentUserHand &&
      getUserHandSolution({ cards: currentUserHand.cards, gameHand: controller.gameHand })
    )
  }, [currentUserHand?.cards, controller.gameHand.visibleCards.length])
  const [isShowingRaise, setIsShowingRaise] = useState(false)
  useEffect(() => {
    if (controller.currentUserId !== controller.gameHand.nextUserIdToAct) {
      setIsShowingRaise(false)
    }
  }, [controller.currentUserId === controller.gameHand.nextUserIdToAct])
  const currentUserData = getCurrentUserData(controller)
  const currentUserId = currentUserData.id
  const betOption = controller.userBetOptions(currentUserId)
  const callButtonRef = useRef<HTMLButtonElement>(null)
  const raiseButtonRef = useRef<HTMLButtonElement>(null)
  const raiseInputRef = useRef<HTMLInputElement>(null)
  useHotkeys('c', () => {
    if (callButtonRef.current) {
      callButtonRef.current.focus()
    }
  })
  const foldButtonRef = useRef<HTMLButtonElement>(null)
  useHotkeys('f', () => {
    if (foldButtonRef.current) {
      foldButtonRef.current.focus()
    }
  })
  useHotkeys('r', () => {
    setIsShowingRaise((bool) => !bool)
    if (betOption === 'ALL_IN') {
      if (raiseButtonRef.current) {
        raiseButtonRef.current.focus()
      }
    } else {
      if (raiseInputRef.current) {
        raiseInputRef.current.focus()
      }
      setIsShowingRaise(true)
    }
  })
  const allDisabled =
    gameMockType === 'disable-actions' ||
    !betOption ||
    !!controller.gameHand.finalResult ||
    !currentUserData.isNextToAct
  if (!currentUserHand && !globalDebugger.hasTestingEnvironmentUX) {
    return null
  }
  const amountToCall = controller.amountRemainingForUserToCall(currentUserData.id)
  const canManuallyShowHand =
    gameMockType !== 'disable-actions' &&
    getCanManuallyShowHand(controller.gameHand, controller.currentUserId)
  const hasFloatingRaise = measurements.windowWidth < numericBreakpoints[3]
  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: 140 }}>
      <Box
        sx={
          hasFloatingRaise
            ? {
                position: 'absolute',
                transition: 'top .3s ease-out',
                top: isShowingRaise ? 0 : -170,
                width: '100%',
                left: 0,
                bg: 'background',
                zIndex: 2,
                height: '100%',
                p: [2, null, 4],
              }
            : { opacity: isShowingRaise ? 1 : 0, transition: 'opacity .3s ease-out', mb: 2, mt: 3 }
        }
      >
        <RaiseOption
          disabled={allDisabled}
          {...{
            controller,
            actions,
            currentUserData,
            raiseInputRef,
            setIsShowingRaise: hasFloatingRaise ? setIsShowingRaise : undefined,
          }}
        />
      </Box>
      <Flex
        mt={hasFloatingRaise ? [3, null, 5] : 0}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: [400, null, null, null, 500],

          mx: 'auto',
          px: 2,
        }}
      >
        <Box
          mr={[1, null, null, null, 5]}
          sx={{ opacity: isShowingRaise ? 0 : 1, transition: 'top .3s ease-out' }}
        >
          {!!canManuallyShowHand ? (
            <AsyncActionButton
              loadingVariant="light"
              action={() =>
                getFunction('showCurrentUsersCards')({
                  gameHandId: controller.gameHand.id,
                  gameId: controller.game.id,
                })
              }
              errorLabel="showCurrentUsersCards"
              sx={{
                ...actionButtonStyles,
                bg: 'lightGreen',
                borderColor: 'lightGreen',
                textShadow: `1px 1px 6px rgba(0,0,0,.4)`,
              }}
            >
              Show Hand
            </AsyncActionButton>
          ) : (
            <Box>
              <AsyncActionButton
                loadingVariant="light"
                ref={callButtonRef}
                disabled={allDisabled || !controller.canUserCheck(currentUserId)}
                action={() => actions.check()}
                errorLabel="CurrentUserHand check"
                sx={{ ...actionButtonStyles, mb: [4, null, null, null, 5] }}
              >
                Check
              </AsyncActionButton>
              <AsyncActionButton
                loadingVariant="light"
                ref={foldButtonRef}
                disabled={allDisabled}
                action={() => actions.fold()}
                errorLabel="CurrentUserHand fold"
                sx={{ ...actionButtonStyles, bg: 'red', borderColor: 'red' }}
              >
                Fold
              </AsyncActionButton>
            </Box>
          )}
        </Box>
        <Box>
          {currentUserHand && (
            <Box>
              {currentUserHand.cards.map((card) => (
                <PlayingCard
                  width={[
                    measurements.playingCardWidth * 1.7,
                    measurements.playingCardWidth * 1.7,
                    measurements.playingCardWidth * 1.7,
                    measurements.playingCardWidth * 1.7,
                    measurements.playingCardWidth * 1.3,
                  ]}
                  sx={{ m: 2, fontSize: [4, null, 5] }}
                  key={card}
                  card={card}
                />
              ))}
            </Box>
          )}
          {solution && (
            <Txt
              sx={{
                textAlign: 'center',
                px: 2,
                fontWeight: 'bold',
                fontSize: [0, 0, 1, 2],
              }}
            >
              {solution.descr}
            </Txt>
          )}
        </Box>
        <Box
          ml={[1, null, null, null, 5]}
          sx={{
            transition: 'top .3s ease-out',
            position: 'relative',
          }}
        >
          {isShowingRaise ? (
            <Box sx={{ textAlign: 'left' }}>
              <Button
                sx={{
                  bg: 'background',
                  color: 'darkBlue',
                  mb: [4, null, null, null, 5],
                  px: 4,
                }}
                onClick={() => setIsShowingRaise(false)}
              >
                Close
              </Button>
            </Box>
          ) : betOption === 'ALL_IN' ? (
            <AsyncActionButton
              loadingVariant="light"
              ref={callButtonRef}
              disabled={allDisabled || isShowingRaise}
              sx={{
                ...actionButtonStyles,
                mb: [4, null, null, null, 5],
              }}
              errorLabel="CurrentUserHand bet"
              action={() =>
                actions.bet({
                  chipsAmount: currentUserData.amountLeftToBet,
                })
              }
            >
              All In (
              <FormattedCurrency amount={currentUserData.amountLeftToBet} />)
            </AsyncActionButton>
          ) : (
            <Button
              disabled={allDisabled || isShowingRaise}
              sx={{
                ...actionButtonStyles,
                mb: [4, null, null, null, 5],
              }}
              onClick={() => setIsShowingRaise(!isShowingRaise)}
            >
              {betOption === 'BET' ? 'Bet' : 'Raise'}
            </Button>
          )}
          <AsyncActionButton
            sx={{
              ...actionButtonStyles,
              opacity: isShowingRaise ? '0 !important' : 1,
              fontSize: [0, null, 2],
            }}
            loadingVariant="light"
            ref={callButtonRef}
            disabled={allDisabled || isShowingRaise || !controller.canUserCall(currentUserId)}
            errorLabel="CurrentUserHand bet"
            action={() =>
              actions.bet({
                chipsAmount: amountToCall,
              })
            }
          >
            Call <FormattedCurrency amount={amountToCall} />
          </AsyncActionButton>
        </Box>
      </Flex>
    </Box>
  )
}
